<template>
  <div class="settings-card">
    <div class="settings-card__header ops-header">
      <div>
        <div class="settings-card__title">{{ $t('configurations.operations.title') }}</div>
        <div class="settings-card__subtitle">
          {{ $t('configurations.operations.subtitle', { assetClass: assetClassName }) }}
        </div>
      </div>
      <b-button variant="outline-secondary" data-test="ops-refresh" @click="refresh">
        {{ $t('configurations.operations.refresh') }}
      </b-button>
    </div>

    <b-alert :show="!!listError" variant="danger" class="mb-3" data-test="ops-list-error">
      {{ listError ? $t(listError) : '' }}
    </b-alert>

    <div class="ops-filters" data-test="ops-execution-filters">
      <b-form-group :label="$t('configurations.operations.filters.kind')" label-size="sm">
        <b-form-select v-model="executionFilters.kind" :options="kindOptions" size="sm" />
      </b-form-group>
      <b-form-group :label="$t('configurations.operations.filters.company')" label-size="sm">
        <b-form-select v-model="executionFilters.company" :options="companyOptions(executionCompanies)" size="sm" />
      </b-form-group>
      <b-form-group :label="$t('configurations.operations.filters.state')" label-size="sm">
        <b-form-select v-model="executionFilters.state" :options="stateOptions" size="sm" />
      </b-form-group>
      <b-form-group :label="$t('configurations.operations.filters.subject')" label-size="sm">
        <b-form-input
          v-model="executionFilters.subject"
          :placeholder="$t('configurations.operations.filters.subjectHint')"
          size="sm"
          @keyup.enter="loadExecutions(1)"
        />
      </b-form-group>
      <b-form-group :label="$t('configurations.operations.filters.startedFrom')" label-size="sm">
        <b-form-input v-model="executionFilters.startedFrom" type="date" size="sm" />
      </b-form-group>
      <b-form-group :label="$t('configurations.operations.filters.startedTo')" label-size="sm">
        <b-form-input v-model="executionFilters.startedTo" type="date" size="sm" />
      </b-form-group>
      <div class="ops-filters__actions">
        <b-button size="sm" variant="primary" data-test="ops-execution-search" @click="loadExecutions(1)">
          {{ $t('configurations.operations.filters.search') }}
        </b-button>
        <b-button size="sm" variant="outline-secondary" @click="resetExecutionFilters">
          {{ $t('configurations.operations.filters.reset') }}
        </b-button>
      </div>
    </div>

    <b-table
      :items="executions"
      :fields="executionFields"
      :busy="loadingExecutions"
      show-empty
      small
      responsive
      hover
      class="ops-table ops-table--clickable"
      data-test="ops-executions-table"
      @row-clicked="openDetail"
    >
      <template #empty>
        <div class="ops-empty">{{ $t('configurations.operations.emptyExecutions') }}</div>
      </template>
      <template #cell(id)="row">
        <span class="ops-mono">{{ row.item.id }}</span>
      </template>
      <template #cell(kind)="row">
        <span class="ops-tag ops-tag--neutral">{{ kindLabel(row.item.kind) }}</span>
      </template>
      <template #cell(subject)="row">
        <div class="ops-ellipsis" :title="row.item.subjectKey">
          {{ subjectTitle(row.item) }}
          <span v-if="row.item.isSubjectDeleted" class="ops-tag ops-tag--neutral">
            {{ $t('configurations.operations.subject.deleted') }}
          </span>
        </div>
        <div v-if="subjectReference(row.item)" class="ops-mono ops-muted">{{ subjectReference(row.item) }}</div>
      </template>
      <template #cell(startedAt)="row">
        <span class="ops-mono">{{ formatUtc(row.item.startedAt) }}</span>
      </template>
      <template #cell(duration)="row">
        <span class="ops-mono">{{ durationOf(row.item.startedAt, row.item.finishedAt) }}</span>
      </template>
      <template #cell(state)="row">
        <span class="ops-tag" :class="'ops-tag--' + stateTone(row.item.state)">
          {{ $t('configurations.operations.states.' + row.item.state) }}
        </span>
      </template>
      <template #cell(stoppedAt)="row">
        <span
          v-if="stoppedAt(row.item)"
          v-b-tooltip.hover
          :title="stepLabel(row.item.kind, row.item.step)"
          class="ops-mono"
          data-test="ops-stopped-at"
          >{{ row.item.step }}</span
        >
      </template>
      <template #cell(failureCount)="row">
        <span v-if="row.item.failureCount > 0" class="ops-tag ops-tag--danger">{{ row.item.failureCount }}</span>
        <span v-else class="ops-muted">0</span>
      </template>
    </b-table>

    <div v-if="executionPaging.totalItems > 0" class="ops-pager">
      <span class="ops-muted">{{ pagerText(executionPaging) }}</span>
      <b-pagination
        v-if="executionPaging.totalPages > 1"
        :value="executionPaging.pageIndex"
        :total-rows="executionPaging.totalItems"
        :per-page="executionPaging.pageSize"
        size="sm"
        class="mb-0"
        @change="loadExecutions"
      />
    </div>

    <b-modal
      v-model="showDetail"
      :title="detail ? $t('configurations.operations.detail.title', { id: detail.id }) : ''"
      :ok-title="$t('configurations.operations.detail.close')"
      ok-only
      ok-variant="outline-secondary"
      size="xl"
      scrollable
      data-test="ops-detail"
    >
      <b-alert :show="!!detailError" variant="danger">{{ detailError ? $t(detailError) : '' }}</b-alert>

      <div v-if="detail">
        <dl class="ops-summary">
          <div>
            <dt>{{ $t('configurations.operations.fields.kind') }}</dt>
            <dd>{{ kindLabel(detail.kind) }}</dd>
          </div>
          <div>
            <dt>{{ $t('configurations.operations.fields.company') }}</dt>
            <dd>{{ detail.company }}</dd>
          </div>
          <div>
            <dt>{{ $t('configurations.operations.fields.state') }}</dt>
            <dd>
              <span class="ops-tag" :class="'ops-tag--' + stateTone(detail.state)">
                {{ $t('configurations.operations.states.' + detail.state) }}
              </span>
            </dd>
          </div>
          <div>
            <dt>{{ $t('configurations.operations.fields.subject') }}</dt>
            <dd>
              <div>
                {{ subjectTitle(detail) }}
                <span v-if="detail.isSubjectDeleted" class="ops-tag ops-tag--neutral">
                  {{ $t('configurations.operations.subject.deleted') }}
                </span>
              </div>
              <div v-if="subjectReference(detail)" class="ops-mono ops-muted">{{ subjectReference(detail) }}</div>
            </dd>
          </div>
          <div>
            <dt>{{ $t('configurations.operations.fields.subjectKey') }}</dt>
            <dd class="ops-mono">{{ detail.subjectKey }}</dd>
          </div>
          <div>
            <dt>{{ $t('configurations.operations.fields.startedAt') }}</dt>
            <dd class="ops-mono">{{ formatUtc(detail.startedAt) }}</dd>
          </div>
          <div>
            <dt>{{ $t('configurations.operations.fields.finishedAt') }}</dt>
            <dd class="ops-mono">{{ formatUtc(detail.finishedAt) || '—' }}</dd>
          </div>
          <div>
            <dt>{{ $t('configurations.operations.fields.stoppedAt') }}</dt>
            <dd v-if="stoppedAt(detail)">
              <div>{{ stepLabel(detail.kind, detail.step) }}</div>
              <div class="ops-mono ops-muted">{{ detail.step }}</div>
            </dd>
            <dd v-else>—</dd>
          </div>
          <div>
            <dt>{{ $t('configurations.operations.fields.operationId') }}</dt>
            <dd class="ops-mono">{{ detail.operationId || '—' }}</dd>
          </div>
        </dl>

        <div class="ops-section-title">
          {{ $t('configurations.operations.detail.failures', { count: detail.failures.length }) }}
        </div>
        <div v-if="detail.failures.length === 0" class="ops-muted mb-3">
          {{ $t('configurations.operations.detail.noFailures') }}
        </div>
        <div v-for="failure in detail.failures" :key="failure.id" class="ops-failure" data-test="ops-failure">
          <div class="ops-failure__head">
            <span class="ops-mono">#{{ failure.id }}</span>
            <span class="ops-tag ops-tag--neutral">{{ failure.stage }}</span>
            <span class="ops-tag ops-tag--danger">{{ failure.reason }}</span>
            <span class="ops-tag" :class="failure.isBlocking ? 'ops-tag--warning' : 'ops-tag--neutral'">
              {{
                failure.isBlocking
                  ? $t('configurations.operations.detail.blocking')
                  : $t('configurations.operations.detail.notBlocking')
              }}
            </span>
            <span class="ops-mono ops-muted ml-auto">{{ formatUtc(failure.occurredAt) }} UTC</span>
          </div>
          <dl class="ops-summary ops-summary--compact">
            <div v-if="failure.isin">
              <dt>ISIN</dt>
              <dd class="ops-mono">{{ failure.isin }}</dd>
            </div>
            <div v-if="failure.doxaIdentifier">
              <dt>{{ $t('configurations.operations.detail.doxaIdentifier') }}</dt>
              <dd class="ops-mono">{{ failure.doxaIdentifier }}</dd>
            </div>
            <div v-if="failure.issuerCode">
              <dt>{{ $t('configurations.operations.detail.issuer') }}</dt>
              <dd class="ops-mono">{{ failure.issuerCode }}</dd>
            </div>
            <div v-if="failure.productName">
              <dt>{{ $t('configurations.operations.detail.product') }}</dt>
              <dd>{{ failure.productName }}</dd>
            </div>
            <div v-if="failure.referenceDate">
              <dt>{{ $t('configurations.operations.detail.referenceDate') }}</dt>
              <dd class="ops-mono">{{ formatDay(failure.referenceDate) }}</dd>
            </div>
          </dl>
          <pre v-if="failure.detail" class="ops-code">{{ failure.detail }}</pre>
        </div>

        <div class="ops-section-title">{{ $t('configurations.operations.detail.steps') }}</div>
        <table v-if="detailSteps" class="table table-sm ops-table mb-0" data-test="ops-steps">
          <thead>
            <tr>
              <th>{{ $t('configurations.operations.steps.step') }}</th>
              <th>{{ $t('configurations.operations.steps.startedAt') }}</th>
              <th>{{ $t('configurations.operations.steps.finishedAt') }}</th>
              <th>{{ $t('configurations.operations.fields.duration') }}</th>
              <th>{{ $t('configurations.operations.steps.items') }}</th>
              <th>{{ $t('configurations.operations.steps.failed') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(step, index) in detailSteps" :key="index" :class="{ 'ops-step--open': !step.finishedAt }">
              <td>
                <div>{{ stepLabel(detail.kind, step.step) }}</div>
                <div class="ops-mono ops-muted">{{ step.step }}</div>
              </td>
              <td class="ops-mono">{{ formatUtc(step.startedAt) }}</td>
              <td>
                <span v-if="step.finishedAt" class="ops-mono">{{ formatUtc(step.finishedAt) }}</span>
                <span v-else class="ops-tag ops-tag--warning">{{
                  $t('configurations.operations.steps.notEnded')
                }}</span>
              </td>
              <td class="ops-mono">{{ durationOf(step.startedAt, step.finishedAt) }}</td>
              <td class="ops-mono">{{ step.total !== null ? `${step.done}/${step.total}` : '—' }}</td>
              <td>
                <span v-if="step.failed > 0" class="ops-tag ops-tag--danger">{{ step.failed }}</span>
                <span v-else class="ops-muted">0</span>
              </td>
            </tr>
          </tbody>
        </table>
        <pre v-else-if="detail.steps" class="ops-code">{{ prettyJson(detail.steps) }}</pre>
        <div v-else class="ops-muted">—</div>

        <div class="ops-section-title">{{ $t('configurations.operations.detail.context') }}</div>
        <dl
          v-if="detailContext && detailContext.length > 0"
          class="ops-summary ops-summary--compact"
          data-test="ops-context"
        >
          <div v-for="[key, value] in detailContext" :key="key">
            <dt>{{ contextLabel(key) }}</dt>
            <dd>{{ value === null || value === undefined || value === '' ? '—' : value }}</dd>
          </div>
        </dl>
        <pre v-else-if="detail.context && !detailContext" class="ops-code">{{ prettyJson(detail.context) }}</pre>
        <div v-else class="ops-muted">{{ $t('configurations.operations.detail.noContext') }}</div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  EXECUTION_STATES,
  KINDS,
  durationOf,
  formatDay,
  formatUtc,
  describeSubject,
  parseContext,
  parseSteps,
  prettyJson,
  searchBody,
} from '../../utils/operationsBackoffice'

const GENERIC_ERROR = 'common.genericError'
const EMPTY_EXECUTION_FILTERS = () => ({
  kind: null,
  company: null,
  state: null,
  subject: '',
  startedFrom: '',
  startedTo: '',
})
const EMPTY_PAGING = () => ({ pageIndex: 1, pageSize: 0, totalPages: 0, totalItems: 0 })

export default {
  name: 'Operations',
  data() {
    return {
      listError: null,

      executionFilters: EMPTY_EXECUTION_FILTERS(),
      executions: [],
      executionCompanies: [],
      executionPaging: EMPTY_PAGING(),
      loadingExecutions: false,

      showDetail: false,
      detail: null,
      detailError: null,
    }
  },
  computed: {
    assetClassName() {
      const slug = this.$store.getters.getSelectedAssetClass
      return this.$t(`navbar.assetClass.${slug}.name`)
    },
    kindOptions() {
      return [this.anyOption(), ...KINDS.map((kind) => ({ value: kind, text: this.kindLabel(kind) }))]
    },
    stateOptions() {
      return [
        this.anyOption(),
        ...EXECUTION_STATES.map((state) => ({
          value: state,
          text: this.$t('configurations.operations.states.' + state),
        })),
      ]
    },
    executionFields() {
      const label = (key) => this.$t('configurations.operations.fields.' + key)
      return [
        { key: 'id', label: label('id') },
        { key: 'kind', label: label('kind') },
        { key: 'company', label: label('company') },
        { key: 'subject', label: label('subject') },
        { key: 'startedAt', label: label('startedAt') },
        { key: 'duration', label: label('duration') },
        { key: 'state', label: label('state') },
        { key: 'stoppedAt', label: label('stoppedAt') },
        { key: 'failureCount', label: label('failures') },
      ]
    },
    detailSteps() {
      return this.detail ? parseSteps(this.detail.steps) : null
    },
    detailContext() {
      return this.detail ? parseContext(this.detail.context) : null
    },
  },
  methods: {
    formatUtc,
    formatDay,
    durationOf,
    prettyJson,
    anyOption() {
      return { value: null, text: this.$t('configurations.operations.filters.any') }
    },
    companyOptions(companies) {
      return [this.anyOption(), ...companies.map((company) => ({ value: company, text: company }))]
    },
    kindLabel(kind) {
      return KINDS.includes(kind) ? this.$t('configurations.operations.kinds.' + kind) : kind
    },
    // The step an execution stopped in. A successful one always ends in the last step of its kind, which says
    // nothing: only an execution that did not succeed has one worth showing.
    stoppedAt(execution) {
      return execution.state !== 'Succeeded' && execution.step ? execution.step : null
    },
    // The words the user reads for a step while waiting (`operations.<kind>.steps`); the code when there are none.
    stepLabel(kind, code) {
      const key = `operations.${kind.charAt(0).toLowerCase()}${kind.slice(1)}.steps.${code}`
      return this.$te(key) ? this.$t(key) : code
    },
    contextLabel(key) {
      const labelKey = 'configurations.operations.contextKeys.' + key
      return this.$te(labelKey) ? this.$t(labelKey) : key
    },
    // `Running` is never offered as a filter (an execution is written when it ends), but a row without a result
    // would still be shown with its own tone.
    stateTone(state) {
      return { Running: 'info', Succeeded: 'success', Failed: 'danger', Released: 'warning' }[state] || 'neutral'
    },
    // What the execution refers to, by its name when there is one: the test, the product, the certificate of a
    // failed import. Below it, subjectReference says which one it is (the id, or the issuer of the certificate).
    subjectTitle(execution) {
      const subject = describeSubject(execution)
      if (subject.name) {
        return subject.name
      }
      if (subject.type === 'lost') {
        return this.$t('configurations.operations.subject.lost')
      }
      return subject.type === 'attempt' ? subject.identifier : this.subjectIdLabel(subject) || execution.subjectKey
    },
    subjectReference(execution) {
      const subject = describeSubject(execution)
      if (subject.type === 'attempt') {
        return subject.issuerCode || ''
      }
      return subject.name ? this.subjectIdLabel(subject) : ''
    },
    subjectIdLabel(subject) {
      if (subject.id === null || subject.id === undefined || Number.isNaN(subject.id)) {
        return ''
      }
      return subject.type === 'test' || subject.type === 'product'
        ? this.$t(`configurations.operations.subject.${subject.type}`, { id: subject.id })
        : ''
    },
    pagerText(paging) {
      const from = (paging.pageIndex - 1) * paging.pageSize + 1
      const to = Math.min(paging.pageIndex * paging.pageSize, paging.totalItems)
      return this.$t('configurations.operations.pager', { from, to, total: paging.totalItems })
    },
    loadExecutions(page) {
      this.listError = null
      this.loadingExecutions = true
      return this.post('/api/backoffice/operations/executions/search', searchBody(this.executionFilters, page))
        .then((resp) => {
          this.executions = resp.items || []
          this.executionCompanies = resp.companies || []
          this.executionPaging = resp.paging || EMPTY_PAGING()
        })
        .catch(() => {
          this.listError = GENERIC_ERROR
        })
        .finally(() => {
          this.loadingExecutions = false
        })
    },
    resetExecutionFilters() {
      this.executionFilters = EMPTY_EXECUTION_FILTERS()
      return this.loadExecutions(1)
    },
    // Reloads the list on the page it shows.
    refresh() {
      return this.loadExecutions(this.executionPaging.pageIndex || 1)
    },
    openDetail(execution) {
      this.detail = null
      this.detailError = null
      this.showDetail = true
      return this.get(`/api/backoffice/operations/executions/${execution.id}`, {})
        .then((resp) => {
          this.detail = { ...resp, failures: resp.failures || [] }
        })
        .catch(() => {
          this.detailError = GENERIC_ERROR
        })
    },
  },
  created() {
    return this.loadExecutions(1)
  },
}
</script>

<style lang="scss" scoped>
// Fairmat design system tokens (colors_and_type.css), as in the issuers back office: the app is Vue 2 +
// bootstrap-vue, so the page follows the system's tokens, type scale and patterns.
.settings-card {
  --fm-text-default: #0f172a;
  --fm-text-muted: #64748b;
  --fm-border: #e2e8f0;
  --fm-surface-50: #f8fafc;
  --fm-surface-100: #f1f5f9;
  --fm-font-mono: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;

  background: #fff;
  border: 1px solid var(--fm-border);
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  color: var(--fm-text-default);
  font-size: 14px;
  line-height: 20px;
}
.settings-card__header {
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--fm-border);
}
.ops-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
// H2 / section title: 16/24 · 600
.settings-card__title {
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: var(--fm-text-default);
}
.settings-card__subtitle {
  margin-top: 4px;
  font-size: 14px;
  line-height: 20px;
  color: var(--fm-text-muted);
}
// Filter bar: fields side by side, labels 12/16 muted, actions aligned to the inputs.
.ops-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 16px;

  /deep/ .form-group {
    margin-bottom: 0;
    min-width: 150px;
  }
  /deep/ legend,
  /deep/ label {
    font-size: 12px;
    line-height: 16px;
    font-weight: 600;
    color: var(--fm-text-muted);
    padding-bottom: 4px;
  }
}
.ops-filters__actions {
  display: flex;
  gap: 8px;
}
.ops-table {
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 0;
  color: var(--fm-text-default);

  // DataTable density: cells 8×12, header 12/16 · 600 on a slate-50 band.
  /deep/ th {
    padding: 8px 12px;
    background: var(--fm-surface-50);
    border-top: 0;
    border-bottom: 1px solid var(--fm-border);
    font-size: 12px;
    line-height: 16px;
    font-weight: 600;
    color: var(--fm-text-muted);
    vertical-align: middle;
    white-space: nowrap;
  }
  /deep/ td {
    padding: 8px 12px;
    border-top: 0;
    border-bottom: 1px solid var(--fm-border);
    vertical-align: middle;
  }
  /deep/ tbody tr:hover td {
    background: var(--fm-surface-50);
  }
}
.ops-table--clickable /deep/ tbody tr {
  cursor: pointer;
}
// Machine values read in the table: JetBrains Mono 13px, no background.
.ops-mono {
  font-family: var(--fm-font-mono);
  font-size: 13px;
  line-height: 20px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.ops-ellipsis {
  display: inline-block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
}
.ops-muted {
  color: var(--fm-text-muted);
}
// Tag: 20px, 12/16 · 600, text always present (status is never colour only).
.ops-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid transparent;
  &--success {
    background: #f0fdf4;
    border-color: #bbf7d0;
    color: #166534;
  }
  &--danger {
    background: #fef2f2;
    border-color: #fecaca;
    color: #991b1b;
  }
  &--warning {
    background: #fffbeb;
    border-color: #fde68a;
    color: #92400e;
  }
  &--info {
    background: #eff6ff;
    border-color: #bfdbfe;
    color: #1e40af;
  }
  &--neutral {
    background: var(--fm-surface-100);
    border-color: var(--fm-border);
    color: #475569;
  }
}
.ops-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 12px;
  font-size: 12px;
  line-height: 16px;
}
// Empty state lives inside the table: neutral, title naming the cause.
.ops-empty {
  padding: 24px 0;
  text-align: center;
  font-weight: 600;
  color: var(--fm-text-muted);
}
// Detail: a grid of label/value pairs, then the sections.
.ops-summary {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px 24px;
  margin-bottom: 24px;

  dt {
    font-size: 12px;
    line-height: 16px;
    font-weight: 600;
    color: var(--fm-text-muted);
  }
  dd {
    margin: 2px 0 0;
    word-break: break-all;
  }
  &--compact {
    margin: 8px 0;
    gap: 8px 24px;
  }
}
.ops-section-title {
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  margin: 16px 0 8px;
}
// The step an execution stopped in (no end): the row is marked, the tag says it in words.
.ops-step--open td {
  background: #fffbeb;
}
.ops-failure {
  border: 1px solid var(--fm-border);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
}
.ops-failure__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
// Code block: technical text and stored JSON, scrollable, never wider than the modal.
.ops-code {
  margin: 0;
  padding: 12px;
  max-height: 320px;
  overflow: auto;
  background: var(--fm-surface-50);
  border: 1px solid var(--fm-border);
  border-radius: 6px;
  font-family: var(--fm-font-mono);
  font-size: 12px;
  line-height: 18px;
  color: var(--fm-text-default);
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
